/**
 * 统一认证重定向工具
 *
 * 用于实现统一登录入口：所有非 home 模块未登录时都跳转到 home 的登录页
 *
 * 退出登录跳转地址：useLogout -> getHomePageUrl() -> getHomeOrigin()
 *
 * Home 的 origin 来源（按优先级）：
 * 1. 环境变量 VITE_HOME_ORIGIN（需在使用 shared 的应用里配置，如 frontend/app/.env.local）
 *    - 由 Vite 在构建时注入到 import.meta.env，只有以 VITE_ 开头的变量才会暴露
 *    - 若未配置或构建时未加载到该 .env，则走下面的 hostname 推导
 * 2. 按当前页面 hostname 推导（见 getHomeOrigin 内逻辑）
 */

const getEnvHomeOrigin = (): string | undefined =>
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { VITE_HOME_ORIGIN?: string } }).env?.VITE_HOME_ORIGIN) || undefined;

/**
 * 获取 Home 模块的 origin（不含路径）
 * 优先使用 VITE_HOME_ORIGIN；否则按 hostname 推导，生产环境使用 www 子域
 */
function getHomeOrigin(): string {
  if (typeof window === 'undefined') return '';

  const currentOrigin = window.location.origin;
  const currentHostname = window.location.hostname;

  const envHome = getEnvHomeOrigin();
  if (envHome) {
    const origin = envHome.replace(/\/$/, '');
    if (import.meta.env?.DEV) {
      console.debug('[auth-redirect] getHomeOrigin: 使用 .env 配置 VITE_HOME_ORIGIN →', origin);
    }
    return origin;
  }

  if (currentHostname === 'app.his.test' || currentHostname === 'mobile.his.test') {
    const origin = 'http://home.his.test:5173';
    if (import.meta.env?.DEV) {
      console.debug('[auth-redirect] getHomeOrigin: 未读到 VITE_HOME_ORIGIN，按 hostname 推导 →', origin);
    }
    return origin;
  }
  if (currentHostname === 'home.his.test') {
    return currentOrigin;
  }
  if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
    return 'http://localhost:5173';
  }
  if (currentHostname.includes('.')) {
    const parts = currentHostname.split('.');
    if (parts.length >= 2) {
      parts[0] = 'www';
      return `${window.location.protocol}//${parts.join('.')}`;
    }
    return currentOrigin;
  }
  return currentOrigin;
}

/**
 * 获取 Home 模块的登录页 URL
 *
 * @param returnUrl - 登录成功后的返回 URL（可选）
 * @returns Home 模块的登录页完整 URL
 */
export function getHomeLoginUrl(returnUrl?: string): string {
  if (typeof window === 'undefined') return '/login';

  const homeOrigin = getHomeOrigin();
  let loginUrl = `${homeOrigin}/login`;

  // 如果提供了返回 URL，添加为查询参数
  if (returnUrl) {
    const encodedReturnUrl = encodeURIComponent(returnUrl);
    loginUrl += `?returnUrl=${encodedReturnUrl}`;
  }

  return loginUrl;
}

/**
 * 获取 Home 模块的注册页 URL
 *
 * @param returnUrl - 注册成功后的返回 URL（可选）
 * @returns Home 模块的注册页完整 URL
 */
export function getHomeRegisterUrl(returnUrl?: string): string {
  if (typeof window === 'undefined') return '/register';

  const homeOrigin = getHomeOrigin();
  let registerUrl = `${homeOrigin}/register`;

  if (returnUrl) {
    const encodedReturnUrl = encodeURIComponent(returnUrl);
    registerUrl += `?returnUrl=${encodedReturnUrl}`;
  }

  return registerUrl;
}

/**
 * 重定向到 Home 模块的登录页
 * 
 * @param includeReturnUrl - 是否包含当前页面作为返回 URL（默认 true）
 */
export function redirectToHomeLogin(includeReturnUrl = true): void {
  if (typeof window === 'undefined') return;

  const returnUrl = includeReturnUrl ? window.location.href : undefined;
  const loginUrl = getHomeLoginUrl(returnUrl);

  // 使用 window.location.href 进行跨域跳转
  window.location.href = loginUrl;
}

/**
 * 重定向到 Home 模块的注册页
 * 
 * @param includeReturnUrl - 是否包含当前页面作为返回 URL（默认 true）
 */
export function redirectToHomeRegister(includeReturnUrl = true): void {
  if (typeof window === 'undefined') return;

  const returnUrl = includeReturnUrl ? window.location.href : undefined;
  const registerUrl = getHomeRegisterUrl(returnUrl);

  window.location.href = registerUrl;
}

/**
 * 从 URL 查询参数中获取返回 URL
 * 
 * @returns 返回 URL（如果存在）
 */
export function getReturnUrlFromQuery(): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  return params.get('returnUrl');
}

/**
 * 获取 Home 模块的主页 URL
 *
 * @returns Home 模块的主页完整 URL
 */
export function getHomePageUrl(): string {
  if (typeof window === 'undefined') return '/';
  const homeOrigin = getHomeOrigin();
  return `${homeOrigin}/`;
}

/**
 * 重定向到 Home 模块的主页
 * 
 * @returns Home 主页 URL（用于后续操作）
 */
export function redirectToHomePage(): string | null {
  if (typeof window === 'undefined') return null;

  const homePageUrl = getHomePageUrl();
  window.location.href = homePageUrl;
  return homePageUrl;
}

/**
 * 登录成功后重定向到返回 URL 或默认页面
 * 
 * @param defaultUrl - 如果没有返回 URL，使用的默认 URL（默认 '/'）
 */
export function redirectAfterLogin(defaultUrl = '/'): void {
  if (typeof window === 'undefined') return;

  const returnUrl = getReturnUrlFromQuery();

  if (returnUrl) {
    // 如果是完整 URL（跨域），使用 window.location.href
    if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
      window.location.href = returnUrl;
    } else {
      // 如果是相对路径，使用 window.location.href（也可以使用 navigate）
      window.location.href = returnUrl;
    }
  } else {
    // 没有返回 URL，跳转到默认页面
    window.location.href = defaultUrl;
  }
}

